import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ISelectOption } from '../dynamic-forms/models/select-option';
import { ReferenceData } from './reference-data';
import { map, catchError, shareReplay } from 'rxjs/operators';
import { ISelectOptionGroup } from '../dynamic-forms/models/select-option-group';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { ApiUrls } from '../utility/apiurls-shared';
import { IOptionGroup } from '../dynamic-forms/models/option-group';
import * as _ from 'lodash';
import { filter } from 'lodash';
import { SelectHelpers } from '../utility/select-helpers';

@Injectable({ providedIn: 'root' })
export class ReferenceDataService {
  private httpClient = inject(HttpClient);
  private apiUrls = inject(ApiUrls);

  private referenceData: Observable<ISelectOptionGroup[]>[] = [];
  private status$ = new BehaviorSubject<string>('');
  status = this.status$.asObservable();

  
 
  

  // public userTitle$ = new Subject<ISelectOptionGroup[]>();

  addStatus(str: string) {
    this.status$.next(str);
  }

  getReferenceData(
    type: string,
    nameFormatter?: (obj: object) => string,
  ): Observable<ISelectOptionGroup[]> {
    //-3;

    this.ensureReferenceDataLoaded(type);

    const list = this.referenceData[type] as BehaviorSubject<ISelectOptionGroup[]>;
    if (nameFormatter) {
      return list.pipe(
        map(response =>
          _.map(response, group => {
            group.options = _.map(group.options, option => {
              option.name = nameFormatter(option.data);
              return option;
            });
            return group;
          }),
        ),
      );
    } else {
      return list;
    }
  }

  getReferenceDataById(type: string, id: number): Observable<ReferenceData> {
    return this.httpClient.get<ReferenceData>(this.apiUrls.reference.getById(type, id));
  }

  getUsStates(): Observable<ISelectOptionGroup[]> {
    return this.httpClient.get<ReferenceData[]>(this.apiUrls.reference.getUsStates()).pipe(
      map(
        states =>
          SelectHelpers.toSelectList(
            states,
            state => state.name,
            state => state.code,
          ),
        shareReplay(1),
      ),
    );
  }
  getPhysicianNameByNpi(npi: string): Observable<ReferenceData> {
    return this.httpClient.get<ReferenceData>(this.apiUrls.reference.getPhysicianNameByNpi(npi));
  }
  getPhysicianNameByFromNpiRegistry(npi: string): Observable<any> {
    return this.httpClient.get<any>(this.apiUrls.reference.getPhysicianNameByFromNpiRegistry(npi));
  }
  private ensureReferenceDataLoaded(type: string) {
    if (this.referenceData[type] == null) {
      this.referenceData[type] = this.httpClient
        .get<ReferenceData[]>(this.apiUrls.reference.get(type))
        .pipe(
          map((res: ReferenceData[]) => {
            const options: ISelectOption[] = res.map(item => ({
              group: item.group ? item.group : type,
              groupSortOrder: item.groupSortOrder ? item.groupSortOrder : 1,
              name: item.name,
              // Not all reference data has an ID field, in this case we set name as the ID
              value: item.id || item.code || item.name,
              data: item,
              sortOrder: item.sortOrder,
              isDeleted: item.isDeleted,
              parentId: item.foreignId,
              parentCategoryId: item.parentCategoryId,
              code: item.code,
            }));

            // NWC-4259 Since the Set() method doesn't handle de-duplication of objects very well,
            // _.uniqWith is used to do deep object comparison.
            const groups: IOptionGroup[] = _.uniqWith(
              Array.from(
                new Set(
                  options.map((item: ISelectOption) => ({
                    name: item.group,
                    sortOrder: item.groupSortOrder,
                  })),
                ),
              ),
              _.isEqual,
            );

            const optionGroups: ISelectOptionGroup[] = groups
              .sort()
              .map((item: ISelectOptionGroup) => ({
                name: item.name,
                sortOrder: item.sortOrder,
                options: options.filter((i: ISelectOption) => i.group === item.name),
              }));

            return optionGroups;
          }),
          catchError(this.handleError),
        );
    }
  }

  getFilteredEntityTypes(ignoredEntityTypes: string[]): Observable<ISelectOptionGroup[]> {
    const type = 'entityType';
    if (ignoredEntityTypes == null) {
      ignoredEntityTypes = [];
    }
    return this.getReferenceData(type).pipe(
      map(res => {
        res[0].options = filter(res[0].options, x => !ignoredEntityTypes.includes(x.code));
        return res;
      }),
    );
  }

  private handleError(error: HttpErrorResponse): Observable<any> {
    switch (error.status) {
      case 404:
        console.error(`Reference data type not found. ${error.error}`);
        break;
      case 500:
        console.error(`The reference data service experienced a problem. ${error.error}`);
        break;
      default:
        console.error(
          `The requested reference data was not returned. HTTP Status: ${error.status}, Message: ${error.error}`,
        );
    }

    return throwError(error);
  }
}
